import React from 'react';
import { useDeviceFlow } from '@/hooks/useDeviceFlow';
import { DeviceCodeDisplay } from './DeviceCodeDisplay';

const TestEndpointButton: React.FC = () => {
  const testProtectedEndpoint = async () => {
    try {
      // Get API base URL from config service
      const { configService } = await import('../services/configService');
      const config = await configService.getConfig();
      
      const response = await fetch(`${config.api.baseUrl}/api/test-protected`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        alert(`✅ Test successful!\n\nExpected 401 Unauthorized response received.\nStatus: ${response.status}\nThis confirms the endpoint is properly protected.`);
      } else {
        const data = await response.json().catch(() => ({ error: 'Unknown response' }));
        alert(`❌ Unexpected response!\n\nStatus: ${response.status}\nResponse: ${JSON.stringify(data, null, 2)}\n\nExpected: 401 Unauthorized`);
      }
    } catch (error) {
      alert(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <button onClick={testProtectedEndpoint} className="test-endpoint-button">
      Test Protected Endpoint (Should get 401)
    </button>
  );
};

export const DeviceAuthFlow: React.FC = () => {
  const { deviceInfo, polling, error, progressMessage, initiateAuth, cancelAuth } = useDeviceFlow();

  if (error) {
    return (
      <div className="auth-container">
        <div className="error-state">
          <h2>Authentication Error</h2>
          <div className="error-message">{error}</div>
          <button onClick={initiateAuth} className="auth-button">
            Try Again
          </button>
        </div>
        
        <style>{`
          .auth-container {
            max-width: 500px;
            margin: 0 auto;
            padding: 1rem;
            background: white;
            border-radius: 6px;
            box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
            margin-top: 1rem;
          }

          .error-state {
            text-align: center;
          }

          .error-state h2 {
            color: #d32f2f;
            margin-bottom: 0.75rem;
            font-size: 1.2rem;
          }
        `}</style>
      </div>
    );
  }

  if (!deviceInfo) {
    return (
      <div className="auth-container">
        <div className="auth-prompt">
          <h2>Sign in to continue</h2>
          <p>This application is running in an iframe and requires external authentication for security.</p>
          <p>Click the button below to start the authentication process.</p>
          
          <button onClick={initiateAuth} className="auth-button" disabled={polling}>
            {polling ? 'Initiating...' : 'Sign In with Azure AD'}
          </button>
          
          {progressMessage && (
            <div className="progress-message">
              <div className="spinner"></div>
              <p>{progressMessage}</p>
            </div>
          )}
          
          <div className="test-section">
            <TestEndpointButton />
            <p className="test-info">
              Test the protected API endpoint (requires authentication)
            </p>
          </div>
        </div>
        
        <style>{`
          .auth-container {
            max-width: 500px;
            margin: 0 auto;
            padding: 1rem;
            background: white;
            border-radius: 6px;
            box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
            margin-top: 1rem;
          }

          .auth-prompt {
            text-align: center;
          }

          .auth-prompt h2 {
            color: #333;
            margin-bottom: 0.75rem;
            font-size: 1.2rem;
          }

          .auth-prompt p {
            color: #666;
            margin-bottom: 0.75rem;
            line-height: 1.4;
            font-size: 0.9rem;
          }

          .progress-message {
            margin-top: 0.75rem;
            padding: 0.75rem;
            background: #f8f9fa;
            border-radius: 4px;
          }

          .progress-message p {
            margin: 0.25rem 0 0 0;
            color: #333;
            font-size: 0.9rem;
          }

          .test-section {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid #eee;
            text-align: center;
          }

          .test-endpoint-button {
            background: #28a745;
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 12px;
            cursor: pointer;
            transition: background 0.2s;
            margin-bottom: 0.25rem;
          }

          .test-endpoint-button:hover {
            background: #218838;
          }

          .test-info {
            color: #666;
            font-size: 11px;
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      <DeviceCodeDisplay
        userCode={deviceInfo.user_code}
        verificationUrl={deviceInfo.verification_uri}
        verificationUrlComplete={deviceInfo.verification_uri_complete}
        polling={polling}
        progressMessage={progressMessage}
      />
      
      {polling && (
        <div className="cancel-container">
          <button onClick={cancelAuth} className="cancel-button">
            Cancel Authentication
          </button>
        </div>
      )}
      
      <style>{`
        .cancel-container {
          text-align: center;
          margin-top: 1rem;
        }

        .cancel-button {
          background: #6c757d;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
        }

        .cancel-button:hover {
          background: #5a6268;
        }
      `}</style>
    </div>
  );
};